import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { CITIES, cityUrl } from '@/lib/cities';
import { SEGMENT_LIST } from '@/lib/segments';
import { useRealTestimonials } from '@/lib/useRealTestimonials';
import {
  OG_IMAGE, LandingHeader, LandingHero, LandingDores, LandingFeatures, LandingDepoimentos,
  LandingGarantias, LandingFaq, LandingCtaFinal, LandingLinks,
} from '@/components/landing/LandingBlocks';

const Regioes = ({ city }) => (
  <section className="py-14 px-4 sm:px-6" data-testid="city-regions">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Atendemos todo o comércio de {city.nome} e região</h2>
      <p className="text-slate-400 mb-6 max-w-2xl">Implantação, treinamento e suporte 100% remotos — sem visita técnica, sem taxa de instalação. Sua loja começa a emitir NFC-e no mesmo dia.</p>
      <div className="flex flex-wrap gap-2">
        {city.regioes.map((r) => (
          <span key={r} className="inline-flex items-center gap-1.5 bg-slate-900 border border-slate-800 text-slate-300 px-3 py-1.5 rounded-full text-sm"><MapPin className="w-3.5 h-3.5 text-amber-400" />{r}</span>
        ))}
      </div>
    </div>
  </section>
);

export default function CityLandingPage({ city: key }) {
  const navigate = useNavigate();
  const city = CITIES[key];
  const url = cityUrl(city);
  const reais = useRealTestimonials({ uf: city.uf });
  const ilustrativos = SEGMENT_LIST.flatMap((s) => s.depoimentos).filter((d) => d.cidade.endsWith(`/${city.uf}`));
  const depoimentos = reais.length ? reais.slice(0, 6) : ilustrativos.slice(0, 3);
  const logos = reais.length ? reais.map((r) => ({ nome: r.negocio, logo: r.logo })) : ilustrativos.map((d) => d.negocio).slice(0, 6);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication', name: `nfcesysPdv - Sistema PDV em ${city.nome}`, applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web', url, description: city.description,
        areaServed: { '@type': 'City', name: city.nome, containedInPlace: { '@type': 'State', name: city.uf } },
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'BRL', description: 'Teste grátis por 30 dias' },
        aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '1250' },
      },
      { '@type': 'FAQPage', mainEntity: city.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
      { '@type': 'BreadcrumbList', itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: 'https://nfcesys.com.br/' },
        { '@type': 'ListItem', position: 2, name: `Sistema PDV em ${city.nome}`, item: url },
      ] },
    ],
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid={`city-page-${key}`}>
      <Helmet>
        <title>{city.title}</title>
        <meta name="description" content={city.description} />
        <meta name="keywords" content={`sistema pdv ${city.nome.toLowerCase()}, emissor nfc-e ${city.uf.toLowerCase()}, sistema para loja ${city.nome.toLowerCase()}, automação comercial ${city.nome.toLowerCase()}`} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={city.title} />
        <meta property="og:description" content={city.description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={city.title} />
        <meta name="twitter:description" content={city.description} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <LandingHeader navigate={navigate} />
      <LandingHero badge={`${city.nome} e região metropolitana`} Icon={city.icon} h1a="Sistema PDV em" h1b={city.nome} h1c={`com NFC-e homologada na SEFAZ-${city.uf}`} sub={city.sub} navigate={navigate} />
      <Regioes city={city} />
      <LandingDores titulo={`O comerciante de ${city.nome} conhece bem isso`} dores={city.dores} />
      <LandingFeatures titulo={`Feito para o comércio de ${city.nome}`} features={city.features} />
      <LandingDepoimentos depoimentos={depoimentos} logos={logos} real={reais.length > 0} />
      <LandingGarantias />
      <LandingFaq faq={city.faq} />
      <LandingCtaFinal titulo={`Sua loja em ${city.nome} emitindo NFC-e hoje mesmo`} navigate={navigate} />
      <LandingLinks currentCity={key} />
    </div>
  );
}
